/**
 * Custom Claims System for Firebase Authentication
 *
 * Implements compressed JSON structure with abbreviated keys to fit within Firebase's 1000-character limit.
 * Structure: {"c": <globalClaims>, "m": [{"o": <orgId>, "c": <orgClaims>, "t": <type>}]}
 *
 * IMPORTANT: This file must be kept in sync across all projects:
 * - Hub:    american-drone-network-hub/src/data-models/custom-claims.ts
 * - Admin:  american-drone-network-admin/src/app/core/models/custom-claims.model.ts
 * - Web:    american-drone-network-web/src/app/core/models/custom-claims.model.ts
 * - Mobile: crop-swipe/lib/models/custom_claims.dart
 *
 * The Hub version includes buildCustomClaims() at the bottom which is server-only.
 */

/**
 * Organization membership roles (bitwise values for efficient permission storage)
 * These roles are organization-specific and stored in membership claims (m[].c)
 */
export enum MembershipRoles {
  Pilot = 1 << 0,              // 1
  PilotInCharge = 1 << 1,      // 2
  Owner = 1 << 2,              // 4
  ChiefSupervisor = 1 << 3,    // 8
  Farmer = 1 << 4,             // 16
}

/** Cached numeric values of MembershipRoles for iteration without per-call allocation */
const MEMBERSHIP_ROLE_VALUES = Object.values(MembershipRoles).filter(
  (v): v is MembershipRoles => typeof v === 'number'
);

/**
 * Membership type — distinguishes the kind of organization in the membership.
 * Serialized as a single letter to minimize Firebase token size.
 */
export enum MembershipType {
  Provider = 'p',   // Drone Service Provider organization
  Farmer = 'f',     // Farmer organization
}

/**
 * Global roles (bitwise values for efficient permission storage)
 * These roles are system-wide and stored in the top-level global claims (c)
 */
export enum GlobalRoles {
  Admin = 1 << 0,              // 1
  SuperAdmin = 1 << 1,         // 2
}

/** Cached numeric values of GlobalRoles for iteration without per-call allocation */
const GLOBAL_ROLE_VALUES = Object.values(GlobalRoles).filter(
  (v): v is GlobalRoles => typeof v === 'number'
);

/**
 * Match type for role checking
 */
export enum MatchType {
  Any = 'any',      // User has at least one of the required roles
  All = 'all',      // User has all required roles
  Exact = 'exact',  // User has exactly the required roles (no more, no less)
}

/**
 * Compressed claim membership data as stored in Firebase token
 */
export interface ClaimMembershipData {
  o: string;  // organizationId
  c: number;  // organizationClaims (bitwise MembershipRoles)
  t?: string; // membershipType ('p' = provider, 'f' = farmer)
}

/**
 * Compressed custom claims data as stored in Firebase token.
 * All fields are optional to reflect that older or minimal tokens may omit them.
 */
export interface CustomClaimsData {
  c?: number;                    // globalClaims (bitwise GlobalRoles)
  m?: ClaimMembershipData[];     // memberships array
}

/**
 * Organization membership with bitwise role permissions and membership type
 */
export class ClaimMembership {
  organizationId: string;
  claims: number;
  membershipType: MembershipType;

  constructor(organizationId: string, claims: number = 0, membershipType: MembershipType = MembershipType.Provider) {
    this.organizationId = organizationId;
    this.claims = claims;
    this.membershipType = membershipType;
  }

  hasRole(role: MembershipRoles): boolean {
    return (this.claims & role) === role;
  }

  hasAnyRole(roles: MembershipRoles[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: MembershipRoles[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  getRoles(): MembershipRoles[] {
    const roles: MembershipRoles[] = [];
    for (const role of MEMBERSHIP_ROLE_VALUES) {
      if (this.hasRole(role)) roles.push(role);
    }
    return roles;
  }

  setRole(role: MembershipRoles): void {
    this.claims |= role;
  }

  removeRole(role: MembershipRoles): void {
    this.claims &= ~role;
  }

  toJSON(): ClaimMembershipData {
    const data: ClaimMembershipData = { o: this.organizationId, c: this.claims };
    if (this.membershipType !== MembershipType.Provider) {
      data.t = this.membershipType;
    }
    return data;
  }

  static fromJSON(json: ClaimMembershipData): ClaimMembership {
    return new ClaimMembership(
      json.o,
      json.c,
      (json.t as MembershipType) || MembershipType.Provider
    );
  }
}

/**
 * Custom claims containing global claims and memberships
 */
export class CustomClaims {
  globalClaims: number;
  memberships: ClaimMembership[];

  constructor(globalClaims: number = 0, memberships: ClaimMembership[] = []) {
    this.globalClaims = globalClaims;
    this.memberships = memberships;
  }

  // -- Global role checks --

  hasGlobalRole(role: GlobalRoles): boolean {
    return (this.globalClaims & role) === role;
  }

  hasAnyGlobalRole(roles: GlobalRoles[]): boolean {
    return roles.some(role => this.hasGlobalRole(role));
  }

  hasAllGlobalRoles(roles: GlobalRoles[]): boolean {
    return roles.every(role => this.hasGlobalRole(role));
  }

  getGlobalRoles(): GlobalRoles[] {
    const roles: GlobalRoles[] = [];
    for (const role of GLOBAL_ROLE_VALUES) {
      if (this.hasGlobalRole(role)) roles.push(role);
    }
    return roles;
  }

  isAdmin(): boolean {
    return this.hasGlobalRole(GlobalRoles.Admin) || this.hasGlobalRole(GlobalRoles.SuperAdmin);
  }

  isSuperAdmin(): boolean {
    return this.hasGlobalRole(GlobalRoles.SuperAdmin);
  }

  setGlobalRole(role: GlobalRoles): void {
    this.globalClaims |= role;
  }

  removeGlobalRole(role: GlobalRoles): void {
    this.globalClaims &= ~role;
  }

  // -- Membership checks --

  getMembership(organizationId: string): ClaimMembership | undefined {
    return this.memberships.find(m => m.organizationId === organizationId);
  }

  hasRoleInOrganization(organizationId: string, role: MembershipRoles): boolean {
    return this.getMembership(organizationId)?.hasRole(role) ?? false;
  }

  hasAnyRoleInOrganization(organizationId: string, roles: MembershipRoles[]): boolean {
    return this.getMembership(organizationId)?.hasAnyRole(roles) ?? false;
  }

  hasAllRolesInOrganization(organizationId: string, roles: MembershipRoles[]): boolean {
    return this.getMembership(organizationId)?.hasAllRoles(roles) ?? false;
  }

  setMembership(organizationId: string, claims: number, membershipType: MembershipType = MembershipType.Provider): void {
    const existing = this.getMembership(organizationId);
    if (existing) {
      existing.claims = claims;
      existing.membershipType = membershipType;
    } else {
      this.memberships.push(new ClaimMembership(organizationId, claims, membershipType));
    }
  }

  removeMembership(organizationId: string): void {
    this.memberships = this.memberships.filter(m => m.organizationId !== organizationId);
  }

  /**
   * Get memberships filtered by type
   */
  getMembershipsByType(type: MembershipType): ClaimMembership[] {
    return this.memberships.filter(m => m.membershipType === type);
  }

  // -- Serialization --

  toJSON(): CustomClaimsData {
    const data: CustomClaimsData = {};
    if (this.globalClaims !== 0) {
      data.c = this.globalClaims;
    }
    if (this.memberships.length > 0) {
      data.m = this.memberships.map(m => m.toJSON());
    }
    return data;
  }

  static fromJSON(json: CustomClaimsData | undefined | null): CustomClaims {
    if (!json) {
      return new CustomClaims();
    }

    const globalClaims = json.c ?? 0;
    const memberships = (json.m ?? []).map((m: ClaimMembershipData) => ClaimMembership.fromJSON(m));

    return new CustomClaims(globalClaims, memberships);
  }

  toCompressedString(): string {
    return JSON.stringify(this.toJSON());
  }

  getSize(): number {
    return this.toCompressedString().length;
  }

  validate(): { valid: boolean; length: number; message?: string } {
    const length = this.getSize();

    if (length > 1000) {
      return {
        valid: false,
        length,
        message: `Custom claims exceed 1000-character limit (${length} characters). Reduce number of memberships.`
      };
    }

    return { valid: true, length };
  }
}

